import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import AuthService from "../service/AuthService";
import PluginService from "../service/PluginService";
import type {
	PluginInfo,
	PluginPage,
	PluginDynamicPage,
} from "../service/PluginService";
import PluginI18nService from "../service/PluginI18nService";
import type { PluginTextResolver } from "../service/PluginI18nService";
import LanguageService from "../service/LanguageService";
import type { SupportedLanguage } from "../service/LanguageService";
import Event from "sap/ui/base/Event";
import NavContainer from "sap/m/NavContainer";
import View from "sap/ui/core/mvc/View";
import Component from "sap/ui/core/Component";
import UIComponent from "sap/ui/core/UIComponent";
import ComponentContainer from "sap/ui/core/ComponentContainer";
import NavigationListItem from "sap/tnt/NavigationListItem";
import NavigationList from "sap/tnt/NavigationList";
import EventBus from "sap/ui/core/EventBus";
import ResponsivePopover from "sap/m/ResponsivePopover";
import List from "sap/m/List";
import NotificationListItem from "sap/m/NotificationListItem";
import Button from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import ToolbarSpacer from "sap/m/ToolbarSpacer";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import WebSocketService from "../service/WebSocketService";
import Dialog from "sap/m/Dialog";
import IconTabBar from "sap/m/IconTabBar";
import IconTabFilter from "sap/m/IconTabFilter";
import Input from "sap/m/Input";
import Select from "sap/m/Select";
import Item from "sap/ui/core/Item";
import Label from "sap/m/Label";
import VBox from "sap/m/VBox";
import Text from "sap/m/Text";
import Image from "sap/m/Image";
import MessageToast from "sap/m/MessageToast";
import MessageBox from "sap/m/MessageBox";
import type { UserInfo } from "../service/AuthService";

interface RealtimeNotificationPayload {
	type: string;
	data?: Record<string, unknown>;
	timestamp?: string;
	notification?: PersistedNotification;
}

interface PersistedNotification {
	id: number;
	type: string;
	title: string;
	message: string;
	data?: Record<string, unknown>;
	deepLink?: string | null;
	readAt?: string | null;
	createdAt: string;
}

interface NotificationsResponse {
	notifications: PersistedNotification[];
}

interface NotificationCountResponse {
	count: number;
}

interface NotificationNavigationTarget {
	pluginId: string;
	viewName: string;
	data: Record<string, string>;
}

interface NotificationListEntry {
	id: string;
	backendId?: number;
	title: string;
	description: string;
	authorName: string;
	datetime: string;
	statusText: string;
	statusState: "None" | "Information" | "Error";
	rowHighlight: "None" | "Error";
	priority: "None" | "Low" | "Medium" | "High";
	unread: boolean;
	target?: NotificationNavigationTarget;
}

/**
 * @namespace com.ang.portal.controller
 */
export default class Home extends BaseController {
	private pluginPages = new Map<string, ComponentContainer>();
	private pluginComponents = new Map<
		string,
		UIComponent & { navigateTo?: (view: string) => void }
	>();
	private pluginNavItems = new Map<string, NavigationListItem>();
	private pluginMetaCache = new Map<string, PluginInfo>();
	/**
	 * Nav keys of runtime sub-pages ("plugin:{id}:{view}:{entityId}"). Lets a
	 * detail route highlight its own sidebar entry when one exists, while an
	 * ordinary detail URL (an invoice, say) still highlights the parent view.
	 */
	private dynamicNavKeys = new Set<string>();
	private loadingPluginNav = false;
	private pluginNavReloadQueued = false;
	private _pendingNavParams?: Record<string, string>;
	private notificationPopover: ResponsivePopover | null = null;
	private notificationUnsubscribe: (() => void) | null = null;
	private notificationSequence = 0;
	private pendingNotificationTarget: NotificationNavigationTarget | null = null;
	private suppressNextPopoverItemPress = false;
	private pluginsLoadedPromise: Promise<void> | null = null;
	private profileDialog: Dialog | null = null;
	private originalProfileName = "";
	private originalProfileEmail = "";
	private originalProfileLanguage: SupportedLanguage =
		LanguageService.DEFAULT_LANGUAGE;

	public onInit(): void {
		const userInfo = AuthService.getUserInfo();
		const initials = userInfo?.name
			? userInfo.name
					.split(" ")
					.map((w) => w[0])
					.join("")
					.substring(0, 2)
					.toUpperCase()
			: "?";

		const roles = userInfo?.roles ?? [];
		const permissions = userInfo?.permissions ?? [];
		const canViewNotifications = this.canViewNotificationsForUser(userInfo);

		const oModel = new JSONModel({
			sideNavExpanded: true,
			userName: userInfo?.name || "",
			userCompanyName: userInfo?.company_name || "",
			userInitials: initials,
			// The Plugins management page is system:admin only.
			canViewPlugins: roles.includes("system:admin"),
			canViewUsers:
				roles.includes("system:admin") ||
				roles.includes("users:admin") ||
				permissions.some((p) => p.startsWith("users:")),
			canViewGroups:
				roles.includes("system:admin") ||
				roles.includes("users:admin"),
			canViewSettings: roles.includes("system:admin"),
			canViewAuditLogs: roles.includes("system:admin"),
			canViewDashboard: false,
			canViewNotifications,
			notificationCount: 0,
			notifications: [] as NotificationListEntry[],
			selectedNavKey: "plugins",
			profileName: userInfo?.name || "",
			profileEmail: userInfo?.email || "",
			profileCurrentPassword: "",
			profileNewPassword: "",
			profileConfirmPassword: "",
			profileBusy: false,
		});
		this.setModel(oModel, "home");

		const oRouter = this.getRouter();

		// Default home route — redirects to the user's first available section
		oRouter
			.getRoute("home")!
			.attachPatternMatched(() => this.onHomeRedirect(), this);

		// Admin section routes
		oRouter
			.getRoute("homePlugins")!
			.attachPatternMatched(() => this.onSectionRouteMatched("plugins"), this);
		oRouter
			.getRoute("homeUsers")!
			.attachPatternMatched(() => this.onSectionRouteMatched("users"), this);
		oRouter
			.getRoute("homeGroups")!
			.attachPatternMatched(() => this.onSectionRouteMatched("groups"), this);
		oRouter
			.getRoute("homeSettings")!
			.attachPatternMatched(() => this.onSectionRouteMatched("settings"), this);
		oRouter
			.getRoute("homeAuditLogs")!
			.attachPatternMatched(
				() => this.onSectionRouteMatched("auditLogs"),
				this,
			);
		oRouter
			.getRoute("homeNotifications")!
			.attachPatternMatched(
				() => this.onSectionRouteMatched("notifications"),
				this,
			);

		oRouter
			.getRoute("homeDashboard")!
			.attachPatternMatched(
				() => this.onSectionRouteMatched("dashboard"),
				this,
			);

		// Plugin routes
		oRouter
			.getRoute("homePlugin")!
			.attachPatternMatched(
				(oEvent: Event) => this.onPluginRouteMatched(oEvent),
				this,
			);
		oRouter
			.getRoute("homePluginView")!
			.attachPatternMatched(
				(oEvent: Event) => this.onPluginRouteMatched(oEvent),
				this,
			);
		oRouter
			.getRoute("homePluginDetail")!
			.attachPatternMatched(
				(oEvent: Event) => this.onPluginRouteMatched(oEvent),
				this,
			);

		EventBus.getInstance().subscribe(
			"PluginsChannel",
			"PluginsChanged",
			this._onPluginsChanged,
			this,
		);
		// A plugin publishes this when its runtime sub-pages change (a saved
		// dashboard created, renamed or deleted), so the sidebar follows without
		// a page reload.
		EventBus.getInstance().subscribe(
			"PluginsChannel",
			"PluginPagesChanged",
			this._onPluginPagesChanged,
			this,
		);
		EventBus.getInstance().subscribe(
			"HomeChannel",
			"NavigateHome",
			this._onNavigateHome,
			this,
		);
		EventBus.getInstance().subscribe(
			"HomeChannel",
			"NavigateToPlugin",
			this._onNavigateToPlugin,
			this,
		);
		EventBus.getInstance().subscribe(
			"NotificationsChannel",
			"Refresh",
			this._onNotificationsRefresh,
			this,
		);
		EventBus.getInstance().subscribe(
			"NotificationsChannel",
			"MarkAllRead",
			this._onNotificationsMarkAllRead,
			this,
		);
		EventBus.getInstance().subscribe(
			"NotificationsChannel",
			"Open",
			this._onNotificationsOpen,
			this,
		);
		EventBus.getInstance().subscribe(
			"NotificationsChannel",
			"MarkUnread",
			this._onNotificationsMarkUnread,
			this,
		);
		EventBus.getInstance().subscribe(
			"NotificationsChannel",
			"Delete",
			this._onNotificationsDelete,
			this,
		);
		// A plugin telling us its per-caller entries changed - a dashboard added
		// or deleted, a role reassigned. Without this the sidebar keeps showing
		// them until the next full page load.
		EventBus.getInstance().subscribe(
			"PluginsChannel",
			"PluginPagesChanged",
			this._onPluginPagesChanged,
			this,
		);
	}

	public onExit(): void {
		EventBus.getInstance().unsubscribe(
			"PluginsChannel",
			"PluginPagesChanged",
			this._onPluginPagesChanged,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"PluginsChannel",
			"PluginsChanged",
			this._onPluginsChanged,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"PluginsChannel",
			"PluginPagesChanged",
			this._onPluginPagesChanged,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"HomeChannel",
			"NavigateHome",
			this._onNavigateHome,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"HomeChannel",
			"NavigateToPlugin",
			this._onNavigateToPlugin,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"NotificationsChannel",
			"Refresh",
			this._onNotificationsRefresh,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"NotificationsChannel",
			"MarkAllRead",
			this._onNotificationsMarkAllRead,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"NotificationsChannel",
			"Open",
			this._onNotificationsOpen,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"NotificationsChannel",
			"MarkUnread",
			this._onNotificationsMarkUnread,
			this,
		);
		EventBus.getInstance().unsubscribe(
			"NotificationsChannel",
			"Delete",
			this._onNotificationsDelete,
			this,
		);
		this.teardownNotifications(true);
		this.notificationPopover?.destroy();
		this.notificationPopover = null;
	}

	private _onPluginsChanged(): void {
		void this.loadPluginNavItems();
	}

	/**
	 * One plugin's per-caller entries went stale.
	 *
	 * The cached item has to be dropped, not just re-added: loadPluginNavItems
	 * reuses anything already in pluginNavItems, so leaving it there would
	 * re-show exactly the entries we were told are wrong. Destroying it is what
	 * keeps its child items from leaking.
	 */
	private _onPluginPagesChanged(
		_channel: string,
		_event: string,
		data: object,
	): void {
		const pluginId = (data as { pluginId?: string })?.pluginId;
		if (!pluginId) {
			return;
		}
		const existing = this.pluginNavItems.get(pluginId);
		if (existing) {
			this.pluginNavItems.delete(pluginId);
			existing.destroy();
		}
		void this.loadPluginNavItems();
	}

	private _onNavigateToPlugin(
		_channel: string,
		_event: string,
		data: object,
	): void {
		const payload = data as {
			pluginId: string;
			view?: string;
			params?: Record<string, string>;
		};
		this._pendingNavParams = payload.params ?? {};
		if (payload.view) {
			this.navTo("homePluginView", {
				pluginId: payload.pluginId,
				viewName: payload.view,
			});
		} else {
			this.navTo("homePlugin", { pluginId: payload.pluginId });
		}
	}

	private _onNavigateHome(
		_channel: string,
		_event: string,
		_data: object,
	): void {
		const oModel = this.getModel("home") as JSONModel;
		const canViewPlugins = oModel.getProperty("/canViewPlugins") as boolean;
		const canViewUsers = oModel.getProperty("/canViewUsers") as boolean;

		if (canViewPlugins) {
			this.navTo("homePlugins");
		} else if (canViewUsers) {
			this.navTo("homeUsers");
		} else {
			this.navTo("home");
		}
	}

	private _onNotificationsRefresh(): void {
		void this.loadNotifications();
	}

	private _onNotificationsMarkAllRead(): void {
		void this.markNotificationsRead();
	}

	private _onNotificationsOpen(
		_channel: string,
		_event: string,
		data: object,
	): void {
		void this.openNotificationEntry(data as NotificationListEntry);
	}

	private _onNotificationsMarkUnread(
		_channel: string,
		_event: string,
		data: object,
	): void {
		void this.markNotificationUnread(data as NotificationListEntry);
	}

	private _onNotificationsDelete(
		_channel: string,
		_event: string,
		data: object,
	): void {
		void this.deleteNotification(data as NotificationListEntry);
	}

	// ── Shared auth/model refresh ─────────────────────────────

	private refreshUserModel(): void {
		const userInfo = AuthService.getUserInfo();
		const roles = userInfo?.roles ?? [];
		const permissions = userInfo?.permissions ?? [];
		const initials = userInfo?.name
			? userInfo.name
					.split(" ")
					.map((w) => w[0])
					.join("")
					.substring(0, 2)
					.toUpperCase()
			: "?";

		const oModel = this.getModel("home") as JSONModel;
		const canViewNotifications = this.canViewNotificationsForUser(userInfo);
		oModel.setProperty("/userName", userInfo?.name || "");
		oModel.setProperty("/userCompanyName", userInfo?.company_name || "");
		oModel.setProperty("/userInitials", initials);
		oModel.setProperty("/canViewPlugins", roles.includes("system:admin"));
		oModel.setProperty(
			"/canViewUsers",
			roles.includes("system:admin") ||
				roles.includes("users:admin") ||
				permissions.some((p) => p.startsWith("users:")),
		);
		oModel.setProperty(
			"/canViewGroups",
			roles.includes("system:admin") || roles.includes("users:admin"),
		);
		oModel.setProperty("/canViewSettings", roles.includes("system:admin"));
		oModel.setProperty("/canViewAuditLogs", roles.includes("system:admin"));
		oModel.setProperty("/canViewNotifications", canViewNotifications);
		if (canViewNotifications) {
			this.initializeNotifications();
		} else {
			this.teardownNotifications(false);
		}
	}

	// ── Route handlers ────────────────────────────────────────

	private onHomeRedirect(): void {
		if (!AuthService.isAuthenticated()) {
			this.navTo("login", {}, true);
			return;
		}

		this.refreshUserModel();

		const oModel = this.getModel("home") as JSONModel;
		const canViewPlugins = oModel.getProperty("/canViewPlugins") as boolean;
		const canViewUsers = oModel.getProperty("/canViewUsers") as boolean;

		// Admins can redirect immediately without waiting for plugin load
		if (canViewPlugins) {
			this.navTo("homePlugins", {}, true);
			void this.loadPluginNavItems();
			return;
		}
		if (canViewUsers) {
			this.navTo("homeUsers", {}, true);
			void this.loadPluginNavItems();
			return;
		}

		// Regular plugin users: wait for plugins to load to know if dashboard is available
		void this.loadPluginNavItems().then(() => {
			const canViewDashboard = oModel.getProperty(
				"/canViewDashboard",
			) as boolean;
			if (canViewDashboard) {
				this.navTo("homeDashboard", {}, true);
			} else {
				// No admin access and no plugins — show empty page
				const oNavContainer = this.byId("homeNavContainer") as NavContainer;
				const emptyPage = this.byId("emptyHomePage")?.getId();
				if (emptyPage) {
					oNavContainer.to(emptyPage);
				}
			}
		});
	}

	private onSectionRouteMatched(sectionKey: string): void {
		if (!AuthService.isAuthenticated()) {
			this.navTo("login", {}, true);
			return;
		}

		this.refreshUserModel();

		const oModel = this.getModel("home") as JSONModel;
		if (
			sectionKey === "notifications" &&
			!oModel.getProperty("/canViewNotifications")
		) {
			this.navTo("home", {}, true);
			return;
		}

		oModel.setProperty("/selectedNavKey", sectionKey);

		const viewIdMap: Record<string, string> = {
			plugins: "pluginsPageView",
			users: "usersPageView",
			groups: "groupsPageView",
			settings: "settingsPageView",
			auditLogs: "auditLogsPageView",
			notifications: "notificationsPageView",
			dashboard: "dashboardPageView",
		};

		const viewId = viewIdMap[sectionKey];
		if (!viewId) return;

		const targetView = this.byId(viewId) as View;
		if (!targetView) return;

		const oNavContainer = this.byId("homeNavContainer") as NavContainer;
		const targetId = targetView.getId();
		if (oNavContainer.getCurrentPage()?.getId() !== targetId) {
			oNavContainer.to(targetId);
		}

		const controller = targetView.getController() as {
			refreshPage?: () => void;
		};
		if (typeof controller?.refreshPage === "function") {
			controller.refreshPage();
		}
		void this.loadPluginNavItems();
	}

	private onPluginRouteMatched(oEvent: Event): void {
		if (!AuthService.isAuthenticated()) {
			this.navTo("login", {}, true);
			return;
		}

		this.refreshUserModel();

		const args = oEvent.getParameter("arguments" as never) as {
			pluginId?: string;
			viewName?: string;
			entityId?: string;
		};
		const pluginId = args?.pluginId;
		if (!pluginId) return;

		const viewName = args?.viewName;
		const entityId = args?.entityId;

		const oModel = this.getModel("home") as JSONModel;
		if (viewName) {
			const viewKey = "plugin:" + pluginId + ":" + viewName;
			const detailKey = entityId ? viewKey + ":" + entityId : "";
			oModel.setProperty(
				"/selectedNavKey",
				detailKey && this.dynamicNavKeys.has(detailKey) ? detailKey : viewKey,
			);
		} else {
			oModel.setProperty("/selectedNavKey", "plugin:" + pluginId);
		}

		void this.ensurePluginsLoadedThenNavigate(pluginId, viewName, entityId);
	}

	private async ensurePluginsLoadedThenNavigate(
		pluginId: string,
		viewName?: string,
		entityId?: string,
	): Promise<void> {
		// If plugin metadata isn't loaded yet (direct URL access), load it first
		if (this.pluginMetaCache.size === 0) {
			if (!this.pluginsLoadedPromise) {
				this.pluginsLoadedPromise = this.loadPluginNavItems();
			}
			await this.pluginsLoadedPromise;
			this.pluginsLoadedPromise = null;
		}

		await this.navigateToPlugin(pluginId, viewName, entityId);
	}

	// ── Sidebar navigation ────────────────────────────────────

	public onNavButtonPress(): void {
		const oModel = this.getModel("home") as JSONModel;
		const bExpanded = oModel.getProperty("/sideNavExpanded") as boolean;
		oModel.setProperty("/sideNavExpanded", !bExpanded);
	}

	public onSideNavSelect(oEvent: Event): void {
		const oItem = oEvent.getParameter("item" as never) as NavigationListItem;
		const sKey = oItem.getKey();

		// A selectable parent only expands from its arrow icon, so clicking the
		// row itself would navigate and leave the sub-pages hidden. Open it too.
		if (
			oItem.getItems().length > 0 &&
			oItem.getParent()?.isA("sap.tnt.NavigationList")
		) {
			oItem.setExpanded(true);
		}

		if (sKey === "logout") {
			this.onLogoutPress();
			return;
		}

		// Map sidebar keys to route navigation
		const sectionRouteMap: Record<string, string> = {
			plugins: "homePlugins",
			users: "homeUsers",
			groups: "homeGroups",
			settings: "homeSettings",
			auditLogs: "homeAuditLogs",
			notifications: "homeNotifications",
			dashboard: "homeDashboard",
		};

		if (sectionRouteMap[sKey]) {
			this.navTo(sectionRouteMap[sKey]);
			return;
		}

		if (sKey.startsWith("plugin:")) {
			const parts = sKey.slice("plugin:".length).split(":");
			const pluginId = parts[0];
			const viewName = parts[1];
			// A dynamic entry may carry the id of the thing it opens, so several
			// entries can share one view - see PluginDynamicPage.
			const entityId = parts[2];
			if (viewName && entityId) {
				this.navTo("homePluginDetail", { pluginId, viewName, entityId });
			} else if (viewName) {
				this.navTo("homePluginView", { pluginId, viewName });
			} else {
				// The plugin's TOP entry. Route to its first page the viewer may
				// see - the page a first open lands on - rather than to the bare
				// plugin route. That route reaches navigateToPlugin with no view,
				// and for an already-loaded plugin that deliberately keeps
				// whatever page is showing (a notification opens a plugin that
				// way and then targets a record). So from the side nav, clicking
				// a plugin's name while on one of its inner pages did nothing.
				//
				// Decided HERE, not in navigateToPlugin, so only a side-nav click
				// changes: that function is shared with route matching and
				// notifications, which rely on the no-view behaviour.
				const firstView = this.firstVisiblePageView(pluginId);
				if (firstView) {
					this.navTo("homePluginView", { pluginId, viewName: firstView });
				} else {
					this.navTo("homePlugin", { pluginId });
				}
			}
		}
	}

	/**
	 * The first page of a plugin this viewer may SEE, or undefined.
	 *
	 * The same rule navigateToPlugin uses for a first open (maySeePage over the
	 * manifest's pages, in order), so a top-entry click and a first open land
	 * on the same page. Undefined for a single-view plugin with no pages.
	 */
	private firstVisiblePageView(pluginId: string): string | undefined {
		const plugin = this.pluginMetaCache.get(pluginId);
		const pages = Array.isArray(plugin?.pages) ? plugin.pages : [];
		const visible = pages.filter((page) => this.maySeePage(page));
		return visible.length > 0 ? visible[0].view : undefined;
	}

	public onLogoutPress(): void {
		this.teardownNotifications(true);
		this.cleanupPlugins();
		AuthService.logout();
		this.navTo("login", {}, true);
	}

	public onNotificationsPress(oEvent: Event): void {
		const source = oEvent.getSource() as Button;
		this.getNotificationPopover().openBy(source);
	}

	public onNotificationsRefresh(): void {
		void this.loadNotifications();
	}

	public onMarkAllNotificationsRead(): void {
		void this.markNotificationsRead();
	}

	public onNotificationTableItemPress(oEvent: Event): void {
		void this.onNotificationItemPress(oEvent);
	}

	private async loadPluginNavItems(): Promise<void> {
		// A request arriving mid-load is remembered rather than dropped: the load
		// in flight may have read the plugin's pages before whatever just changed
		// them, so ignoring it would leave a deleted entry in the sidebar.
		if (this.loadingPluginNav) {
			this.pluginNavReloadQueued = true;
			return;
		}
		this.loadingPluginNav = true;

		try {
			const userInfo = AuthService.getUserInfo();
			const roles = userInfo?.roles ?? [];
			// Derived from the user's roles and permissions by the backend — plugins
			// are not assigned, so this is simply what their grants reach.
			const userPlugins = userInfo?.plugins ?? [];
			const canSeeAll = roles.includes("system:admin");

			const allPlugins = await PluginService.getPlugins();
			const enabledPlugins = allPlugins.filter((p) => p.enabled);
			const visiblePlugins = enabledPlugins.filter(
				(p) => canSeeAll || userPlugins.includes(p.id),
			);

			const oModel = this.getModel("home") as JSONModel;
			oModel.setProperty("/canViewDashboard", visiblePlugins.length > 0);

			this.pluginMetaCache.clear();
			this.dynamicNavKeys.clear();
			for (const p of visiblePlugins) {
				this.pluginMetaCache.set(p.id, p);
			}

			const navList = this.byId("mainNavigationList") as NavigationList;
			const oNavContainer = this.byId("homeNavContainer") as NavContainer;
			const currentIds = new Set(visiblePlugins.map((p) => p.id));

			// Remove nav items for plugins that are no longer visible
			// and clean up cached pages
			for (const [pluginId, navItem] of this.pluginNavItems) {
				if (!currentIds.has(pluginId)) {
					navList.removeItem(navItem);
					navItem.destroy();
					this.pluginNavItems.delete(pluginId);

					const cachedContainer = this.pluginPages.get(pluginId);
					if (cachedContainer) {
						oNavContainer.removePage(cachedContainer);
						cachedContainer.destroy();
						this.pluginPages.delete(pluginId);
					}
				}
			}

			// Remove all plugin nav items from the list so we can re-add
			// them in the correct order (API returns sorted by sort_order)
			for (const [, navItem] of this.pluginNavItems) {
				navList.removeItem(navItem);
			}

			// Re-add in the order returned by the API
			for (const plugin of visiblePlugins) {
				let item = this.pluginNavItems.get(plugin.id);
				// Runtime pages are user data and change between loads, so a
				// plugin that supplies them gets its nav item rebuilt rather
				// than reused from the cache.
				if (item && plugin.dynamicPages) {
					this.pluginNavItems.delete(plugin.id);
					item.destroy();
					item = undefined;
				}
				if (!item) {
					const hasPages =
						Array.isArray(plugin.pages) && plugin.pages.length > 0;
					// Filtered before anything else reads it, so the i18n resolver
					// does not fetch labels for pages this viewer will not see and
					// the section headings are computed over the visible set - a
					// section whose every page is hidden leaves no orphan heading.
					const pages = hasPages
						? plugin.pages.filter((page) => this.maySeePage(page))
						: [];
					// Manifest labels may be "{i18n>key}" references into the plugin's
					// own bundle; literals pass through unchanged.
					// Per-caller entries, if this plugin serves any. Fetched before
					// the resolver so a failure costs the extra entries and nothing
					// else - getDynamicPages never throws.
					const dynamicRoute = (plugin.dynamicPages ?? "").trim();
					const dynamicPages = dynamicRoute
						? await PluginService.getDynamicPages(plugin.id, dynamicRoute)
						: [];

					const t = await PluginI18nService.getResolver(plugin.id, [
						plugin.displayName,
						...pages.map((page) => page.name),
						// Section labels are manifest strings too, so they resolve
						// through the same bundle as the page names.
						...pages
							.map((page) => page.section)
							.filter((section): section is string => !!section),
					]);
					item = new NavigationListItem({
						text: t(plugin.displayName, plugin.name),
						icon: plugin.icon || "sap-icon://puzzle",
						key: "plugin:" + plugin.id,
						expanded: false,
						items: this.buildPluginPageItems(
							plugin.id,
							pages,
							t,
							dynamicPages,
						),
					});
					this.pluginNavItems.set(plugin.id, item);
				}
				navList.addItem(item);
			}
		} catch {
			// Plugin nav loading failure should not block the shell
		} finally {
			this.loadingPluginNav = false;
		}

		if (this.pluginNavReloadQueued) {
			this.pluginNavReloadQueued = false;
			await this.loadPluginNavItems();
		}
	}

	/**
	 * A plugin's pages as navigation items, with optional section labels.
	 *
	 * A page may declare `section`; consecutive pages naming the same one are
	 * preceded by a single non-selectable label item. Grouping is by consecutive
	 * run rather than by gathering every page with that name, so the manifest's
	 * order stays the order on screen and a plugin cannot have its pages
	 * silently rearranged.
	 *
	 * The labels are SIBLINGS of the pages, not parents: sap.tnt renders a
	 * second-level item alone and never its subtree (see
	 * NavigationListItem.renderSecondLevelNavItem), so a page nested under a
	 * label inside a plugin would not draw at all.
	 *
	 * A manifest with no sections produces exactly the flat list it always did.
	 */
	/**
	 * May this viewer SEE this page in the side navigation?
	 *
	 * A page with no `roles` is visible to anyone who can reach the plugin,
	 * which is what every page meant before the field existed. `system:admin`
	 * qualifies for everything, so a portal administrator never has to be named
	 * in a manifest to reach a plugin's own admin pages.
	 *
	 * Visibility only. The plugin's routes are what refuse the DATA, and a page
	 * hidden here is still reachable by typing its URL - it will simply have
	 * nothing to show. Anything that must not be read has to be guarded server
	 * side as well; this just stops the menu offering it.
	 */
	private maySeePage(page: PluginPage): boolean {
		const required = Array.isArray(page.roles) ? page.roles : [];
		if (required.length === 0) return true;

		// The same source the canView* flags above read, so one answer about who
		// this viewer is rather than two that can disagree.
		const held = new Set(
			(AuthService.getUserInfo()?.roles ?? []).map((role) =>
				String(role).toLowerCase(),
			),
		);
		if (held.has("system:admin")) return true;

		return required.some((role) => held.has(String(role).toLowerCase()));
	}

	private buildPluginPageItems(
		pluginId: string,
		pages: PluginPage[],
		t: PluginTextResolver,
		dynamicPages: PluginDynamicPage[] = [],
	): NavigationListItem[] {
		const items: NavigationListItem[] = [];
		let openSection: string | undefined;

		// Dynamic entries REPLACE the static page naming the same view, and are
		// appended when they name a view the manifest never declared. Both
		// behaviours are wanted: a plugin whose dashboard is really "the CEO
		// template" wants its one static entry renamed in place, while a plugin
		// listing a user's own dashboards wants several new ones. Replacing in
		// place is what keeps the manifest's order the order on screen.
		const dynamicByView = new Map<string, PluginDynamicPage[]>();
		for (const entry of dynamicPages) {
			const list = dynamicByView.get(entry.view) ?? [];
			list.push(entry);
			dynamicByView.set(entry.view, list);
		}
		const staticViews = new Set(pages.map((page) => page.view));

		const dynamicItem = (entry: PluginDynamicPage): NavigationListItem =>
			new NavigationListItem({
				// A per-caller label is already resolved text, never an
				// "{i18n>key}" reference: only the manifest can name a bundle key.
				text: entry.name,
				icon: entry.icon || "sap-icon://document",
				key:
					"plugin:" +
					pluginId +
					":" +
					entry.view +
					(entry.entityId ? ":" + entry.entityId : ""),
			});

		for (const page of pages) {
			const section = (page.section ?? "").trim();

			if (section && section !== openSection) {
				const label = new NavigationListItem({
					text: t(page.section, section),
					// A caption, not a destination: it names no view, so it must
					// not be selectable or focusable as a target.
					selectable: false,
					key: "plugin:" + pluginId + ":section:" + section,
				});
				// NavigationListItem is an Item, not a Control, so it has no
				// addStyleClass. A data attribute is the hook instead; the
				// second-level renderer emits it via openStart("li", this).
				label.data("ang-nav-section", "true", true);
				items.push(label);
			}
			// A page with no section ends the run, so the next sectioned page
			// re-emits its label rather than appearing to belong to the last one.
			openSection = section || undefined;

			const replacements = dynamicByView.get(page.view);
			if (replacements && replacements.length > 0) {
				for (const entry of replacements) {
					items.push(dynamicItem(entry));
				}
			} else {
				items.push(
					new NavigationListItem({
						text: t(page.name, page.view),
						icon: page.icon || "sap-icon://document",
						key: "plugin:" + pluginId + ":" + page.view,
					}),
				);
			}
		}

		// Entries for views the manifest never declared go last, in the order the
		// plugin returned them.
		for (const entry of dynamicPages) {
			if (!staticViews.has(entry.view)) {
				items.push(dynamicItem(entry));
			}
		}

		return items;
	}

	// ── Plugin component loading ──────────────────────────────

	private async navigateToPlugin(
		pluginId: string,
		viewName?: string,
		entityId?: string,
	): Promise<void> {
		const oNavContainer = this.byId("homeNavContainer") as NavContainer;
		const plugin = this.pluginMetaCache.get(pluginId);
		if (!plugin) return;

		const cachedContainer = this.pluginPages.get(pluginId);

		if (cachedContainer) {
			oNavContainer.to(cachedContainer.getId());
			const comp = (
				cachedContainer as unknown as {
					getComponentInstance(): UIComponent | undefined;
				}
			).getComponentInstance() as
				| (UIComponent & { navigateTo?: (view: string, data?: object) => void })
				| undefined;
			if (comp && typeof comp.navigateTo === "function") {
				const navData: Record<string, unknown> = {};
				if (entityId) navData.entityId = entityId;
				if (this._pendingNavParams) {
					navData.params = this._pendingNavParams;
					this._pendingNavParams = undefined;
				}
				if (viewName || Object.keys(navData).length > 0) {
					comp.navigateTo(viewName ?? "", navData);
				}
				this.applyPendingNotificationTarget(pluginId, comp, viewName, entityId);
			}
			return;
		}

		// For multi-page plugins with no explicit view, default to the first page
		// this viewer may actually SEE. Taking pages[0] blindly would open a
		// plugin on a page that is not in the caller's own menu - for a plugin
		// whose first page is admin-only, every non-admin would land on a screen
		// refusing them, with no way back to it in the nav.
		const visiblePages = (
			Array.isArray(plugin.pages) ? plugin.pages : []
		).filter((page) => this.maySeePage(page));
		const initialView =
			viewName || (visiblePages.length > 0 ? visiblePages[0].view : undefined);

		// Build the shellNavigate callback so plugins can trigger URL changes.
		// Signature: shellNavigate(targetPluginId?, targetView?, targetEntityId?)
		const shellNavigate = (
			targetPluginId?: string,
			targetView?: string,
			targetEntityId?: string,
		) => {
			const resolvedPluginId = targetPluginId || pluginId;
			if (targetView && targetEntityId) {
				this.navTo("homePluginDetail", {
					pluginId: resolvedPluginId,
					viewName: targetView,
					entityId: targetEntityId,
				});
			} else if (targetView) {
				this.navTo("homePluginView", {
					pluginId: resolvedPluginId,
					viewName: targetView,
				});
			} else {
				this.navTo("homePlugin", { pluginId: resolvedPluginId });
			}
		};

		try {
			const componentData: Record<string, unknown> = { shellNavigate };
			if (initialView) {
				componentData.initialView = initialView;
			}
			if (entityId) {
				componentData.initialEntityId = entityId;
			}
			if (this._pendingNavParams) {
				componentData.initialParams = this._pendingNavParams;
				this._pendingNavParams = undefined;
			}

			const component = (await Component.create({
				name: plugin.namespace,
				url: `/plugins/${plugin.id}/frontend/`,
				componentData,
			})) as UIComponent;

			const container = new ComponentContainer({
				component,
				width: "100%",
				height: "100%",
			});

			oNavContainer.addPage(container);
			this.pluginPages.set(pluginId, container);
			this.pluginComponents.set(
				pluginId,
				component as UIComponent & { navigateTo?: (view: string) => void },
			);
			oNavContainer.to(container.getId());
			this.applyPendingNotificationTarget(
				pluginId,
				component as UIComponent & {
					navigateTo?: (view: string, data?: object) => void;
				},
				initialView,
				entityId,
			);
		} catch (error) {
			const fallbackTitle = "Failed to load plugin component.";
			const normalizedError =
				error instanceof Error
					? {
							name: error.name,
							message: error.message,
							stack: error.stack,
						}
					: error;
			const details = {
				pluginId: plugin.id,
				namespace: plugin.namespace,
				url: `/plugins/${plugin.id}/frontend/`,
				initialView,
				entityId,
				error: normalizedError,
			};

			console.error(
				"[PluginLoadError] Failed to create plugin component",
				details,
			);

			const message =
				error instanceof Error && error.message ? error.message : fallbackTitle;

			MessageBox.error(message, {
				title: fallbackTitle,
				details: JSON.stringify(details, null, 2),
			});

			MessageToast.show(fallbackTitle);
		}
	}

	private applyPendingNotificationTarget(
		pluginId: string,
		component:
			| (UIComponent & { navigateTo?: (view: string, data?: object) => void })
			| undefined,
		viewName?: string,
		entityId?: string,
	): void {
		const target = this.pendingNotificationTarget;
		if (
			!target ||
			target.pluginId !== pluginId ||
			!component ||
			typeof component.navigateTo !== "function"
		) {
			return;
		}

		const targetEntityId = this.asOptionalString(target.data.entityId);
		if (viewName && target.viewName !== viewName) {
			return;
		}

		if (entityId && targetEntityId && targetEntityId !== entityId) {
			return;
		}

		this.pendingNotificationTarget = null;
		component.navigateTo(target.viewName, target.data);
	}

	// ── Profile dialog ────────────────────────────────────────

	public onProfileAvatarPress(): void {
		const oModel = this.getModel("home") as JSONModel;
		oModel.setProperty("/profileCurrentPassword", "");
		oModel.setProperty("/profileNewPassword", "");
		oModel.setProperty("/profileConfirmPassword", "");
		oModel.setProperty("/profileBusy", false);
		oModel.setProperty("/profile2FAEnabled", false);
		oModel.setProperty("/profile2FARequired", false);
		oModel.setProperty("/profile2FAStatusText", "");
		oModel.setProperty("/profile2FABackupText", "");

		// Preselect the language actually in effect, so the dialog is accurate even
		// before the profile request lands (and if it fails).
		this.originalProfileLanguage = LanguageService.getCurrent();
		oModel.setProperty("/profileLanguage", this.originalProfileLanguage);

		// Fetch fresh profile data from server
		void AuthService.authenticatedFetch("/api/profile")
			.then(async (res) => {
				if (res.ok) {
					const profile = (await res.json()) as {
						name: string;
						email: string;
						language?: string;
					};
					this.originalProfileName = profile.name || "";
					this.originalProfileEmail = profile.email || "";
					if (LanguageService.isSupported(profile.language)) {
						this.originalProfileLanguage = profile.language;
						oModel.setProperty("/profileLanguage", profile.language);
					}
				} else {
					const userInfo = AuthService.getUserInfo();
					this.originalProfileName = userInfo?.name || "";
					this.originalProfileEmail = userInfo?.email || "";
				}
				oModel.setProperty("/profileName", this.originalProfileName);
				oModel.setProperty("/profileEmail", this.originalProfileEmail);
			})
			.catch(() => {
				const userInfo = AuthService.getUserInfo();
				this.originalProfileName = userInfo?.name || "";
				this.originalProfileEmail = userInfo?.email || "";
				oModel.setProperty("/profileName", this.originalProfileName);
				oModel.setProperty("/profileEmail", this.originalProfileEmail);
			});

		// Fetch 2FA status
		void this.loadProfile2FAStatus();

		if (!this.profileDialog) {
			this.profileDialog = new Dialog({
				title: "{i18n>profileDialogTitle}",
				contentWidth: "28rem",
				draggable: true,
				content: [
					new IconTabBar({
						expandable: false,
						items: [
							new IconTabFilter({
								text: "{i18n>profileTabDetails}",
								icon: "sap-icon://person-placeholder",
								content: [
									new VBox({
										items: [
											new Label({
												text: "{i18n>profileFieldName}",
												required: true,
											}),
											new Input({
												value: "{home>/profileName}",
												enabled: "{= !${home>/profileBusy}}",
												change: () => {
													const val = (
														(oModel.getProperty("/profileName") as string) || ""
													).trim();
													if (!val) {
														oModel.setProperty(
															"/profileName",
															this.originalProfileName,
														);
													}
												},
											}),
											new Label({ text: "{i18n>profileFieldEmail}" }),
											new Input({
												value: "{home>/profileEmail}",
												type: "Email",
												editable: false,
											}),
											new Label({ text: "{i18n>profileFieldLanguage}" }),
											new Select({
												selectedKey: "{home>/profileLanguage}",
												enabled: "{= !${home>/profileBusy}}",
												width: "100%",
												items: [
													new Item({
														key: "de",
														text: "{i18n>languageGerman}",
													}),
													new Item({
														key: "en",
														text: "{i18n>languageEnglish}",
													}),
												],
											}),
											new Text({
												text: "{i18n>profileLanguageHint}",
											}).addStyleClass("sapUiTinyMarginTop"),
										],
									}).addStyleClass("sapUiSmallMargin"),
								],
							}),
							new IconTabFilter({
								text: "{i18n>profileTabPassword}",
								icon: "sap-icon://key",
								content: [
									new VBox({
										items: [
											new Label({
												text: "{i18n>profileFieldCurrentPassword}",
												required: true,
											}),
											new Input({
												value: "{home>/profileCurrentPassword}",
												type: "Password",
												enabled: "{= !${home>/profileBusy}}",
											}),
											new Label({
												text: "{i18n>profileFieldNewPassword}",
												required: true,
											}),
											new Input({
												value: "{home>/profileNewPassword}",
												type: "Password",
												enabled: "{= !${home>/profileBusy}}",
											}),
											new Label({
												text: "{i18n>profileFieldConfirmPassword}",
												required: true,
											}),
											new Input({
												value: "{home>/profileConfirmPassword}",
												type: "Password",
												enabled: "{= !${home>/profileBusy}}",
											}),
										],
									}).addStyleClass("sapUiSmallMargin"),
								],
							}),
							new IconTabFilter({
								text: "{i18n>profileTabSecurity}",
								icon: "sap-icon://shield",
								content: [
									new VBox({
										items: [
											new Text({ text: "{home>/profile2FAStatusText}" }),
											new Text({
												text: "{home>/profile2FABackupText}",
												visible: "{home>/profile2FAEnabled}",
											}).addStyleClass("sapUiTinyMarginTop"),
											new Button({
												text: "{i18n>profile2FAEnableButton}",
												type: "Emphasized",
												visible: "{= !${home>/profile2FAEnabled}}",
												enabled: "{= !${home>/profileBusy}}",
												press: () => void this.onProfile2FASetup(),
											}).addStyleClass("sapUiSmallMarginTop"),
											new Text({
												text: "{i18n>profile2FARequiredHint}",
												visible: "{home>/profile2FARequired}",
											}).addStyleClass("sapUiTinyMarginTop"),
											new Button({
												text: "{i18n>profile2FADisableButton}",
												type: "Reject",
												// Mandatory 2FA: the backend refuses to turn it off.
												visible:
													"{= ${home>/profile2FAEnabled} && !${home>/profile2FARequired} }",
												enabled: "{= !${home>/profileBusy}}",
												press: () => void this.onProfile2FADisable(),
											}).addStyleClass("sapUiSmallMarginTop"),
											new Button({
												text: "{i18n>profile2FARegenerateButton}",
												visible: "{home>/profile2FAEnabled}",
												enabled: "{= !${home>/profileBusy}}",
												press: () => void this.onProfile2FARegenerate(),
											}).addStyleClass("sapUiTinyMarginTop"),
										],
									}).addStyleClass("sapUiSmallMargin"),
								],
							}),
						],
					}),
				],
				beginButton: new Button({
					text: "{i18n>profileSaveButton}",
					type: "Emphasized",
					press: () => void this.onProfileSave(),
				}),
				endButton: new Button({
					text: "{i18n>profileCancelButton}",
					press: () => this.profileDialog!.close(),
				}),
			});

			this.getView()!.addDependent(this.profileDialog);
		}

		this.profileDialog.open();
	}

	private async onProfileSave(): Promise<void> {
		const oModel = this.getModel("home") as JSONModel;
		const name = ((oModel.getProperty("/profileName") as string) || "").trim();
		const currentPassword =
			(oModel.getProperty("/profileCurrentPassword") as string) || "";
		const newPassword =
			(oModel.getProperty("/profileNewPassword") as string) || "";
		const confirmPassword =
			(oModel.getProperty("/profileConfirmPassword") as string) || "";
		const selectedLanguage = oModel.getProperty(
			"/profileLanguage",
		) as unknown;
		const language = LanguageService.isSupported(selectedLanguage)
			? selectedLanguage
			: this.originalProfileLanguage;

		const bundle = await this.getResourceBundle();

		if (!name) {
			MessageBox.error(bundle.getText("profileNameRequired"));
			return;
		}

		if (newPassword && newPassword !== confirmPassword) {
			MessageBox.error(bundle.getText("profilePasswordMismatch"));
			return;
		}

		if (newPassword && newPassword.length < 6) {
			MessageBox.error(bundle.getText("profilePasswordTooShort"));
			return;
		}

		oModel.setProperty("/profileBusy", true);

		try {
			const payload: Record<string, string> = { name, language };
			if (newPassword) {
				payload.currentPassword = currentPassword;
				payload.newPassword = newPassword;
			}

			const response = await AuthService.authenticatedFetch("/api/profile", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				const errorData = (await response.json().catch(() => ({}))) as {
					message?: string;
				};
				throw new Error(
					errorData.message || bundle.getText("profileUpdateError"),
				);
			}

			const data = (await response.json()) as {
				user: UserInfo;
				accessToken: string;
				refreshToken: string;
			};

			AuthService.storeTokens(data);

			this.refreshUserModel();

			this.originalProfileLanguage = language;
			this.profileDialog!.close();

			// A language change reloads the app so the shell, cached dialogs and
			// already-loaded plugins all come up in the new language. Skip the
			// success toast in that case — the reload would swallow it anyway.
			if (language !== LanguageService.getCurrent()) {
				LanguageService.apply(language);
				return;
			}

			MessageToast.show(bundle.getText("profileUpdateSuccess"));
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: bundle.getText("profileUpdateError");
			MessageBox.error(message);
		} finally {
			oModel.setProperty("/profileBusy", false);
		}
	}

	// ── Profile 2FA management ────────────────────────────────

	private async loadProfile2FAStatus(): Promise<void> {
		const oModel = this.getModel("home") as JSONModel;
		try {
			const status = await AuthService.get2FAStatus();
			oModel.setProperty("/profile2FAEnabled", status.enabled);
			oModel.setProperty("/profile2FARequired", status.required === true);
			const bundle = await this.getResourceBundle();
			oModel.setProperty(
				"/profile2FAStatusText",
				status.enabled
					? bundle.getText("profile2FAEnabled")
					: bundle.getText("profile2FADisabled"),
			);
			if (status.enabled) {
				oModel.setProperty(
					"/profile2FABackupText",
					bundle.getText("profile2FABackupCodesRemaining", [
						String(status.backupCodesRemaining),
					]),
				);
			}
		} catch {
			oModel.setProperty("/profile2FAStatusText", "");
		}
	}

	private async onProfile2FASetup(): Promise<void> {
		const oModel = this.getModel("home") as JSONModel;
		oModel.setProperty("/profileBusy", true);

		try {
			const setupData = await AuthService.setup2FAAuthenticated();
			oModel.setProperty("/profileBusy", false);
			this.showProfile2FASetupDialog(setupData.qrCode, setupData.secret);
		} catch (error) {
			oModel.setProperty("/profileBusy", false);
			const message =
				error instanceof Error ? error.message : "Failed to start 2FA setup.";
			MessageBox.error(message);
		}
	}

	private showProfile2FASetupDialog(qrCode: string, secret: string): void {
		const oModel = this.getModel("home") as JSONModel;
		oModel.setProperty("/profile2FASetupCode", "");

		const oCodeInput = new Input({
			type: "Text",
			maxLength: 10,
			value: "{home>/profile2FASetupCode}",
			placeholder: "Enter 6-digit code",
			width: "100%",
		});

		const setupDialog = new Dialog({
			title: "{i18n>profile2FASetupTitle}",
			contentWidth: "24rem",
			content: [
				new VBox({
					alignItems: "Center",
					items: [
						new Text({
							text: "{i18n>profile2FASetupInstructions}",
							wrapping: true,
						}).addStyleClass("sapUiSmallMarginBottom"),
						new Image({
							src: qrCode,
							width: "200px",
							height: "200px",
						}).addStyleClass("sapUiSmallMarginBottom"),
						new Label({ text: "{i18n>profile2FASetupSecretLabel}" }),
						new Text({ text: secret, wrapping: true }).addStyleClass(
							"sapUiTinyMarginBottom",
						),
						new Label({
							text: "{i18n>profile2FASetupCodeLabel}",
							required: true,
						}).addStyleClass("sapUiSmallMarginTop"),
						oCodeInput,
					],
				}).addStyleClass("sapUiSmallMargin"),
			],
			beginButton: new Button({
				text: "{i18n>profile2FAEnableButton}",
				type: "Emphasized",
				press: async () => {
					const code = (
						(oModel.getProperty("/profile2FASetupCode") as string) || ""
					).trim();
					if (!code || code.length < 6) {
						MessageBox.error(
							(await this.getResourceBundle()).getText(
								"profile2FACodeInvalid",
							),
						);
						return;
					}
					try {
						const result = await AuthService.enable2FA(code);
						setupDialog.close();
						setupDialog.destroy();
						void this.loadProfile2FAStatus();
						MessageBox.information(
							"Save these backup codes securely. They will not be shown again.\n\n" +
								result.backupCodes.join("\n"),
							{ title: "Backup Codes" },
						);
					} catch (error) {
						const msg =
							error instanceof Error ? error.message : "Failed to enable 2FA.";
						MessageBox.error(msg);
					}
				},
			}),
			endButton: new Button({
				text: "{i18n>profileCancelButton}",
				press: () => {
					setupDialog.close();
					setupDialog.destroy();
				},
			}),
			afterOpen: () => {
				setTimeout(() => oCodeInput.focus(), 100);
			},
		});

		this.getView()!.addDependent(setupDialog);
		setupDialog.open();
	}

	private async onProfile2FADisable(): Promise<void> {
		const oModel = this.getModel("home") as JSONModel;
		oModel.setProperty("/profile2FADisablePassword", "");
		oModel.setProperty("/profile2FADisableCode", "");

		const bundle = await this.getResourceBundle();

		const oPasswordInput = new Input({
			type: "Password",
			value: "{home>/profile2FADisablePassword}",
			width: "100%",
		});
		const oCodeInput = new Input({
			type: "Text",
			maxLength: 10,
			value: "{home>/profile2FADisableCode}",
			width: "100%",
		});

		const disableDialog = new Dialog({
			title: bundle.getText("profile2FADisableTitle"),
			contentWidth: "22rem",
			content: [
				new VBox({
					items: [
						new Label({
							text: bundle.getText("profileFieldCurrentPassword"),
							required: true,
						}),
						oPasswordInput,
						new Label({
							text: bundle.getText("profile2FACodeLabel"),
							required: true,
						}).addStyleClass("sapUiSmallMarginTop"),
						oCodeInput,
					],
				}).addStyleClass("sapUiSmallMargin"),
			],
			beginButton: new Button({
				text: bundle.getText("profile2FADisableButton"),
				type: "Reject",
				press: async () => {
					const password = (
						(oModel.getProperty("/profile2FADisablePassword") as string) || ""
					).trim();
					const code = (
						(oModel.getProperty("/profile2FADisableCode") as string) || ""
					).trim();
					if (!password || !code) {
						MessageBox.error(
							(await this.getResourceBundle()).getText(
								"profile2FADisableFieldsRequired",
							),
						);
						return;
					}
					try {
						await AuthService.disable2FA(password, code);
						disableDialog.close();
						disableDialog.destroy();
						void this.loadProfile2FAStatus();
						MessageToast.show("2FA has been disabled.");
					} catch (error) {
						const msg =
							error instanceof Error ? error.message : "Failed to disable 2FA.";
						MessageBox.error(msg);
					}
				},
			}),
			endButton: new Button({
				text: bundle.getText("profileCancelButton"),
				press: () => {
					disableDialog.close();
					disableDialog.destroy();
				},
			}),
		});

		this.getView()!.addDependent(disableDialog);
		disableDialog.open();
	}

	private async onProfile2FARegenerate(): Promise<void> {
		const oModel = this.getModel("home") as JSONModel;
		oModel.setProperty("/profile2FARegenPassword", "");

		const bundle = await this.getResourceBundle();

		const oPasswordInput = new Input({
			type: "Password",
			value: "{home>/profile2FARegenPassword}",
			width: "100%",
		});

		const regenDialog = new Dialog({
			title: bundle.getText("profile2FARegenerateTitle"),
			contentWidth: "22rem",
			content: [
				new VBox({
					items: [
						new Label({
							text: bundle.getText("profileFieldCurrentPassword"),
							required: true,
						}),
						oPasswordInput,
					],
				}).addStyleClass("sapUiSmallMargin"),
			],
			beginButton: new Button({
				text: bundle.getText("profile2FARegenerateButton"),
				type: "Emphasized",
				press: async () => {
					const password = (
						(oModel.getProperty("/profile2FARegenPassword") as string) || ""
					).trim();
					if (!password) {
						MessageBox.error(
							(await this.getResourceBundle()).getText(
								"profile2FAPasswordRequired",
							),
						);
						return;
					}
					try {
						const result = await AuthService.regenerateBackupCodes(password);
						regenDialog.close();
						regenDialog.destroy();
						void this.loadProfile2FAStatus();
						MessageBox.information(
							"Save these backup codes securely. They will not be shown again.\n\n" +
								result.backupCodes.join("\n"),
							{ title: bundle.getText("profile2FABackupCodesTitle") },
						);
					} catch (error) {
						const msg =
							error instanceof Error
								? error.message
								: "Failed to regenerate backup codes.";
						MessageBox.error(msg);
					}
				},
			}),
			endButton: new Button({
				text: bundle.getText("profileCancelButton"),
				press: () => {
					regenDialog.close();
					regenDialog.destroy();
				},
			}),
		});

		this.getView()!.addDependent(regenDialog);
		regenDialog.open();
	}

	private cleanupPlugins(): void {
		const oNavContainer = this.byId("homeNavContainer") as NavContainer;
		const navList = this.byId("mainNavigationList") as NavigationList;

		for (const [, navItem] of this.pluginNavItems) {
			navList.removeItem(navItem);
			navItem.destroy();
		}
		this.pluginNavItems.clear();

		for (const [, container] of this.pluginPages) {
			oNavContainer.removePage(container);
			container.destroy();
		}
		this.pluginPages.clear();
		this.pluginComponents.clear();
		this.pluginMetaCache.clear();
	}

	private hasAdminRole(roles: string[]): boolean {
		const isAdmin =
			roles.includes("system:admin") ||
			roles.some((role) => role.endsWith(":admin"));
		console.log("[Home] hasAdminRole check:", roles, "->", isAdmin);
		return isAdmin;
	}

	private canViewNotificationsForUser(
		userInfo: ReturnType<typeof AuthService.getUserInfo>,
	): boolean {
		// Any authenticated user can receive notifications (from any plugin).
		return Boolean(userInfo);
	}

	private initializeNotifications(): void {
		console.log("[Home] initializeNotifications called");
		void this.loadNotifications();

		if (!this.notificationUnsubscribe) {
			this.notificationUnsubscribe = WebSocketService.on(
				"*",
				(payload: unknown) => {
					void this.handleRealtimeNotification(payload);
				},
			);
		}

		console.log("[Home] Calling WebSocketService.connect()");
		void WebSocketService.connect();
	}

	private async loadNotifications(): Promise<void> {
		if (!AuthService.isAuthenticated()) {
			return;
		}

		try {
			const [notificationsResponse, countResponse] = await Promise.all([
				AuthService.authenticatedFetch("/api/notifications?limit=50"),
				AuthService.authenticatedFetch("/api/notifications/unread-count"),
			]);

			if (!notificationsResponse.ok || !countResponse.ok) {
				throw new Error("Failed to load notifications.");
			}

			const notificationsData =
				(await notificationsResponse.json()) as NotificationsResponse;
			const countData =
				(await countResponse.json()) as NotificationCountResponse;
			const bundle = await this.getResourceBundle();
			const entries = notificationsData.notifications
				.map((notification) =>
					this.buildPersistedNotificationEntry(bundle, notification),
				)
				.filter((entry): entry is NotificationListEntry => Boolean(entry));

			const oModel = this.getModel("home") as JSONModel;
			oModel.setProperty("/notifications", entries);
			oModel.setProperty("/notificationCount", Number(countData.count) || 0);
		} catch (error) {
			console.error("Failed to load notifications:", error);
		}
	}

	private teardownNotifications(disconnectSocket: boolean): void {
		if (this.notificationUnsubscribe) {
			this.notificationUnsubscribe();
			this.notificationUnsubscribe = null;
		}

		if (disconnectSocket) {
			WebSocketService.disconnect();
		}
	}

	private async handleRealtimeNotification(payload: unknown): Promise<void> {
		const notification = this.asRealtimeNotification(payload);
		if (!notification) {
			return;
		}

		const bundle = await this.getResourceBundle();
		const entry = notification.notification
			? this.buildPersistedNotificationEntry(bundle, notification.notification)
			: this.buildNotificationEntry(bundle, notification);
		if (!entry) {
			return;
		}

		const oModel = this.getModel("home") as JSONModel;
		const existing =
			(oModel.getProperty("/notifications") as NotificationListEntry[]) ?? [];
		const matchingExisting = existing.find((candidate) => {
			if (entry.backendId && candidate.backendId === entry.backendId) {
				return true;
			}
			return candidate.id === entry.id;
		});
		const deduped = existing.filter((candidate) => {
			if (entry.backendId && candidate.backendId === entry.backendId) {
				return false;
			}
			return candidate.id !== entry.id;
		});
		oModel.setProperty("/notifications", [entry, ...deduped].slice(0, 50));
		if (entry.unread && !matchingExisting?.unread) {
			oModel.setProperty(
				"/notificationCount",
				((oModel.getProperty("/notificationCount") as number) || 0) + 1,
			);
		}

		await this.showToastForError(undefined, "notificationsToastReceived");
	}

	private asRealtimeNotification(
		payload: unknown,
	): RealtimeNotificationPayload | null {
		if (!payload || typeof payload !== "object") {
			return null;
		}

		const candidate = payload as Record<string, unknown>;
		if (typeof candidate.type !== "string") {
			return null;
		}

		return {
			type: candidate.type,
			data:
				typeof candidate.data === "object" && candidate.data !== null
					? (candidate.data as Record<string, unknown>)
					: {},
			timestamp:
				typeof candidate.timestamp === "string"
					? candidate.timestamp
					: undefined,
			notification: this.asPersistedNotification(candidate.notification),
		};
	}

	private asPersistedNotification(
		value: unknown,
	): PersistedNotification | undefined {
		if (!value || typeof value !== "object") {
			return undefined;
		}

		const candidate = value as Record<string, unknown>;
		const id = Number(candidate.id);
		if (!Number.isFinite(id) || typeof candidate.type !== "string") {
			return undefined;
		}

		return {
			id,
			type: candidate.type,
			title: this.asString(candidate.title, candidate.type),
			message: this.asString(candidate.message, ""),
			data:
				typeof candidate.data === "object" && candidate.data !== null
					? (candidate.data as Record<string, unknown>)
					: {},
			deepLink:
				typeof candidate.deepLink === "string" ? candidate.deepLink : null,
			readAt: typeof candidate.readAt === "string" ? candidate.readAt : null,
			createdAt:
				typeof candidate.createdAt === "string"
					? candidate.createdAt
					: new Date().toISOString(),
		};
	}

	private buildNotificationEntry(
		bundle: ResourceBundle,
		payload: RealtimeNotificationPayload,
	): NotificationListEntry | null {
		const data = payload.data ?? {};
		const target = this.buildNotificationTarget(
			payload,
			payload.notification?.deepLink,
		);
		const invoiceNumber = this.asString(
			data.invoiceNumber,
			bundle.getText("notificationsInvoiceFallback"),
		);
		const timestamp = payload.timestamp
			? new Date(payload.timestamp)
			: new Date();
		const formattedTime = Number.isNaN(timestamp.getTime())
			? new Date().toLocaleString()
			: timestamp.toLocaleString();
		const id = `notification-${++this.notificationSequence}`;

		if (payload.type === "invoice:comment-added") {
			const userName = this.asString(
				data.userName,
				bundle.getText("notificationsUnknownUser"),
			);
			const commentText = this.asString(
				data.text,
				bundle.getText("notificationsCommentFallback"),
			);
			return {
				id,
				title: bundle.getText("notificationsCommentTitle", [invoiceNumber]),
				description: bundle.getText("notificationsCommentDescription", [
					userName,
					commentText,
				]),
				authorName: userName,
				datetime: formattedTime,
				statusText: bundle.getText("notificationsStatusUnread"),
				statusState: "Error",
				rowHighlight: "Error",
				priority: "High",
				unread: true,
				target,
			};
		}

		if (payload.type === "invoice:ticket-created") {
			const createdBy = this.asString(
				data.createdByName,
				bundle.getText("notificationsUnknownUser"),
			);
			const title = this.asString(
				data.title,
				bundle.getText("notificationsTicketFallback"),
			);
			return {
				id,
				title: bundle.getText("notificationsTicketCreatedTitle", [
					invoiceNumber,
				]),
				description: bundle.getText("notificationsTicketCreatedDescription", [
					createdBy,
					title,
				]),
				authorName: createdBy,
				datetime: formattedTime,
				statusText: bundle.getText("notificationsStatusUnread"),
				statusState: "Error",
				rowHighlight: "Error",
				priority: "High",
				unread: true,
				target,
			};
		}

		if (payload.type === "invoice:ticket-updated") {
			const updatedBy = this.asString(
				data.updatedByName,
				bundle.getText("notificationsUnknownUser"),
			);
			const nextStatus = this.asString(
				data.newStatus,
				bundle.getText("notificationsStatusFallback"),
			);
			const title = this.asString(
				data.title,
				bundle.getText("notificationsTicketFallback"),
			);
			return {
				id,
				title: bundle.getText("notificationsTicketUpdatedTitle", [
					invoiceNumber,
				]),
				description: bundle.getText("notificationsTicketUpdatedDescription", [
					updatedBy,
					title,
					nextStatus,
				]),
				authorName: updatedBy,
				datetime: formattedTime,
				statusText: bundle.getText("notificationsStatusUnread"),
				statusState: "Error",
				rowHighlight: "Error",
				priority: "High",
				unread: true,
				target,
			};
		}

		return null;
	}

	private buildPersistedNotificationEntry(
		bundle: ResourceBundle,
		notification: PersistedNotification,
	): NotificationListEntry | null {
		const payload: RealtimeNotificationPayload = {
			type: notification.type,
			data: notification.data ?? {},
			timestamp: notification.createdAt,
		};
		const entry = this.buildNotificationEntry(bundle, payload);
		const baseEntry = entry ?? {
			id: String(notification.id),
			title: notification.title,
			description: notification.message,
			authorName: this.asString(
				notification.data?.userName,
				bundle.getText("notificationsUnknownUser"),
			),
			datetime: this.formatNotificationDate(notification.createdAt),
			statusText: bundle.getText("notificationsStatusUnread"),
			statusState: "Error" as const,
			rowHighlight: "Error" as const,
			priority: "High" as const,
			unread: true,
			target: this.buildNotificationTarget(payload, notification.deepLink),
		};
		const unread = !notification.readAt;

		return {
			...baseEntry,
			id: String(notification.id),
			backendId: notification.id,
			title: notification.title || baseEntry.title,
			description: notification.message || baseEntry.description,
			datetime: this.formatNotificationDate(notification.createdAt),
			statusText: bundle.getText(
				unread ? "notificationsStatusUnread" : "notificationsStatusRead",
			),
			statusState: unread ? "Error" : "None",
			rowHighlight: unread ? "Error" : "None",
			priority: unread ? "High" : "None",
			unread,
		};
	}

	private formatNotificationDate(value: string | undefined): string {
		const timestamp = value ? new Date(value) : new Date();
		return Number.isNaN(timestamp.getTime())
			? new Date().toLocaleString()
			: timestamp.toLocaleString();
	}

	private asString(value: unknown, fallback: string): string {
		return typeof value === "string" && value.trim().length > 0
			? value
			: fallback;
	}

	private asOptionalString(value: unknown): string | undefined {
		if (typeof value === "string" && value.trim().length > 0) {
			return value;
		}

		if (typeof value === "number" && Number.isFinite(value)) {
			return String(value);
		}

		return undefined;
	}

	/**
	 * Fallback for non-invoice notification types: derive a navigation target
	 * from the raw deepLink hash (e.g. "#/home/plugin/Delivery/JobDetail/123"),
	 * matching the homePlugin/homePluginView/homePluginDetail route patterns.
	 */
	private parseDeepLinkTarget(
		deepLink: string | null | undefined,
	): NotificationNavigationTarget | undefined {
		if (!deepLink) {
			return undefined;
		}

		const segments = deepLink
			.replace(/^#\/?/, "")
			.split("/")
			.filter(Boolean);

		if (segments[0] !== "home" || segments[1] !== "plugin" || !segments[2]) {
			return undefined;
		}

		const pluginId = segments[2];
		const viewName = segments[3] ?? "";
		const entityId = segments[4];

		return {
			pluginId,
			viewName,
			data: entityId ? { entityId } : {},
		};
	}

	private buildNotificationTarget(
		payload: RealtimeNotificationPayload,
		deepLink?: string | null,
	): NotificationNavigationTarget | undefined {
		const data = payload.data ?? {};
		const invoiceId = this.asOptionalString(data.invoiceId);
		if (!invoiceId) {
			return this.parseDeepLinkTarget(deepLink);
		}

		const targetData: Record<string, string> = {
			invoiceId,
			entityId: invoiceId,
		};

		// "invoice:comment-reply" is sent to the original (often non-admin)
		// commenter when an admin replies; it carries the same data shape as
		// "invoice:comment-added" (commentId + lineNum), so route both to the
		// comment section / line-comment dialog identically.
		if (
			payload.type === "invoice:comment-added" ||
			payload.type === "invoice:comment-reply"
		) {
			targetData.focusSection = "comments";
			targetData.openTarget = "comment";
			const commentId = this.asOptionalString(data.commentId);
			if (commentId) {
				targetData.commentId = commentId;
			}
			if (data.lineNum !== null && data.lineNum !== undefined) {
				targetData.lineNum = String(data.lineNum);
			}
		}

		if (
			payload.type === "invoice:ticket-created" ||
			payload.type === "invoice:ticket-updated"
		) {
			targetData.focusSection = "tickets";
			targetData.openTarget = "ticket";
			const ticketId = this.asOptionalString(data.ticketId);
			if (ticketId) {
				targetData.ticketId = ticketId;
			}
		}

		return {
			pluginId: "Invoices",
			viewName: "InvoiceDetail",
			data: targetData,
		};
	}

	private async onNotificationItemPress(oEvent: Event): Promise<void> {
		if (this.suppressNextPopoverItemPress) {
			this.suppressNextPopoverItemPress = false;
			return;
		}

		const listItem = oEvent.getParameter("listItem" as never) as
			| NotificationListItem
			| undefined;
		const source = oEvent.getSource() as {
			getBindingContext?: (name: string) => unknown;
		};
		const context =
			listItem?.getBindingContext("home") ?? source.getBindingContext?.("home");
		const entry = (
			context as { getObject?: () => unknown } | undefined
		)?.getObject?.() as NotificationListEntry | undefined;

		if (!entry) {
			return;
		}

		await this.openNotificationEntry(entry);
	}

	private async openNotificationEntry(
		entry: NotificationListEntry,
	): Promise<void> {
		await this.markNotificationRead(entry);

		if (!entry.target) {
			return;
		}

		this.notificationPopover?.close();

		const { pluginId, viewName, data } = entry.target;
		const entityId = this.asOptionalString(data.entityId);
		this.pendingNotificationTarget = entry.target;

		if (viewName && entityId) {
			this.navTo("homePluginDetail", { pluginId, viewName, entityId });
		} else if (viewName) {
			this.navTo("homePluginView", { pluginId, viewName });
		} else {
			this.navTo("homePlugin", { pluginId });
		}

		await this.ensurePluginsLoadedThenNavigate(pluginId, viewName, entityId);

		const container = this.pluginPages.get(pluginId);
		const component = (
			container as unknown as {
				getComponentInstance?: () => UIComponent | undefined;
			}
		)?.getComponentInstance?.() as
			| (UIComponent & { navigateTo?: (view: string, data?: object) => void })
			| undefined;

		this.applyPendingNotificationTarget(
			pluginId,
			component,
			viewName,
			entityId,
		);
	}

	private async markNotificationRead(
		entry: NotificationListEntry,
	): Promise<void> {
		const bundle = await this.getResourceBundle();
		const oModel = this.getModel("home") as JSONModel;
		const notifications = (
			(oModel.getProperty("/notifications") as NotificationListEntry[]) ?? []
		).map((candidate) =>
			candidate.id === entry.id
				? {
						...candidate,
						unread: false,
						statusText: bundle.getText("notificationsStatusRead"),
						statusState: "None" as const,
						rowHighlight: "None" as const,
						priority: "None" as const,
					}
				: candidate,
		);

		oModel.setProperty("/notifications", notifications);
		oModel.setProperty(
			"/notificationCount",
			notifications.filter((candidate) => candidate.unread).length,
		);

		if (!entry.backendId) {
			return;
		}

		try {
			const response = await AuthService.authenticatedFetch(
				`/api/notifications/${entry.backendId}/read`,
				{ method: "PATCH" },
			);
			if (!response.ok) {
				throw new Error("Failed to mark notification read.");
			}
		} catch (error) {
			console.error("Failed to mark notification read:", error);
		}
	}

	private async markNotificationsRead(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const oModel = this.getModel("home") as JSONModel;
		const notifications = (
			(oModel.getProperty("/notifications") as NotificationListEntry[]) ?? []
		).map((entry) => ({
			...entry,
			unread: false,
			statusText: bundle.getText("notificationsStatusRead"),
			statusState: "None" as const,
			rowHighlight: "None" as const,
			priority: "None" as const,
		}));

		oModel.setProperty("/notifications", notifications);
		oModel.setProperty("/notificationCount", 0);

		try {
			const response = await AuthService.authenticatedFetch(
				"/api/notifications/read-all",
				{ method: "PATCH" },
			);
			if (!response.ok) {
				throw new Error("Failed to mark notifications read.");
			}
		} catch (error) {
			console.error("Failed to mark notifications read:", error);
		}
	}

	private async markNotificationUnread(
		entry: NotificationListEntry,
	): Promise<void> {
		const bundle = await this.getResourceBundle();
		const oModel = this.getModel("home") as JSONModel;
		const notifications = (
			(oModel.getProperty("/notifications") as NotificationListEntry[]) ?? []
		).map((candidate) =>
			candidate.id === entry.id
				? {
						...candidate,
						unread: true,
						statusText: bundle.getText("notificationsStatusUnread"),
						statusState: "Information" as const,
						rowHighlight: "Error" as const,
					}
				: candidate,
		);

		oModel.setProperty("/notifications", notifications);
		oModel.setProperty(
			"/notificationCount",
			notifications.filter((candidate) => candidate.unread).length,
		);

		if (!entry.backendId) {
			return;
		}

		try {
			const response = await AuthService.authenticatedFetch(
				`/api/notifications/${entry.backendId}/unread`,
				{ method: "PATCH" },
			);
			if (!response.ok) {
				throw new Error("Failed to mark notification unread.");
			}
		} catch (error) {
			console.error("Failed to mark notification unread:", error);
		}
	}

	private async deleteNotification(entry: NotificationListEntry): Promise<void> {
		const oModel = this.getModel("home") as JSONModel;
		const notifications = (
			(oModel.getProperty("/notifications") as NotificationListEntry[]) ?? []
		).filter((candidate) => candidate.id !== entry.id);

		oModel.setProperty("/notifications", notifications);
		oModel.setProperty(
			"/notificationCount",
			notifications.filter((candidate) => candidate.unread).length,
		);

		if (!entry.backendId) {
			return;
		}

		try {
			const response = await AuthService.authenticatedFetch(
				`/api/notifications/${entry.backendId}`,
				{ method: "DELETE" },
			);
			if (!response.ok) {
				throw new Error("Failed to delete notification.");
			}
		} catch (error) {
			console.error("Failed to delete notification:", error);
		}
	}

	private getPopoverEntryFromEvent(
		oEvent: Event,
	): NotificationListEntry | undefined {
		const source = oEvent.getSource() as {
			getBindingContext?: (name: string) => unknown;
		};
		const context = source.getBindingContext?.("home");
		return (context as { getObject?: () => unknown } | undefined)?.getObject?.() as
			| NotificationListEntry
			| undefined;
	}

	private getNotificationPopover(): ResponsivePopover {
		if (!this.notificationPopover) {
			const list = new List({
				noDataText: "{i18n>notificationsEmpty}",
				itemPress: (oEvent: Event) => {
					void this.onNotificationItemPress(oEvent);
				},
				items: {
					path: "home>/notifications",
					template: new NotificationListItem({
						title: "{home>title}",
						description: "{home>description}",
						authorName: "{home>authorName}",
						datetime: "{home>datetime}",
						priority: "{home>priority}",
						unread: "{home>unread}",
						type: "{= ${home>target} ? 'Navigation' : 'Active' }",
						showCloseButton: true,
						close: (oEvent: Event) => {
							this.suppressNextPopoverItemPress = true;
							const entry = this.getPopoverEntryFromEvent(oEvent);
							if (entry) {
								void this.deleteNotification(entry);
							}
						},
						buttons: [
							new Button({
								icon: "sap-icon://envelope",
								text: "{i18n>notificationsMarkUnread}",
								type: "Transparent",
								tooltip: "{i18n>notificationsMarkUnread}",
								press: (oEvent: Event) => {
									this.suppressNextPopoverItemPress = true;
									const entry = this.getPopoverEntryFromEvent(oEvent);
									if (entry && !entry.unread) {
										void this.markNotificationUnread(entry);
									}
								},
							}),
						],
					}),
				},
			});

			const footer = new Toolbar({
				content: [
					new ToolbarSpacer(),
					new Button({
						text: "{i18n>notificationsMarkAllRead}",
						type: "Transparent",
						enabled: "{= ${home>/notificationCount} > 0 }",
						press: () => {
							void this.markNotificationsRead();
						},
					}),
				],
			});

			this.notificationPopover = new ResponsivePopover({
				title: "{i18n>notificationsPopoverTitle}",
				contentWidth: "28rem",
				contentHeight: "24rem",
				placement: "Bottom",
				content: [list],
				footer,
			});

			this.notificationPopover.setModel(this.getModel("home"), "home");
			this.notificationPopover.setModel(
				this.getOwnerComponent().getModel("i18n"),
				"i18n",
			);
			this.getView().addDependent(this.notificationPopover);
		}

		return this.notificationPopover;
	}
}
